import { useState, useEffect, useRef, useCallback } from 'react';
import { AcsChatService } from './AcsChatService';
import type { AcsChatConfig } from './AcsChatConfig';
import type { ChatMessage } from './ChatService';

// ============================================================================
// useAcsChat – React Hook  (frontend-only)
// ============================================================================
//
// Backend provides: token, userId, threadId.
// This hook does: connect → join thread → load history → send → receive.
//
// Usage in any portal:
//
//   const { messages, sendMessage, isConnected, error } = useAcsChat({
//     config: {
//       endpoint: import.meta.env.VITE_ACS_ENDPOINT,
//       token: backendResponse.token,
//       userId: backendResponse.userId,
//       displayName: 'Dr. Smith',
//     },
//     threadId: backendResponse.threadId,
//   });
//
// ============================================================================

export interface UseAcsChatOptions {
  /** ACS config – endpoint, token, userId, displayName (all from backend except endpoint) */
  config: AcsChatConfig;
  /** Thread ID created by the backend */
  threadId: string;
  /** Auto-connect on mount. Default: true */
  autoConnect?: boolean;
  /** Callback when a new message is received (for notifications) */
  onNewMessage?: (message: ChatMessage) => void;
}

export interface UseAcsChatReturn {
  /** Messages in the thread (mapped to ChatMessage format for the Chat UI) */
  messages: ChatMessage[];
  /** Send a text message */
  sendMessage: (text: string) => Promise<void>;
  /** Whether the chat client is connected and ready */
  isConnected: boolean;
  /** Whether the chat is currently loading (connecting / fetching history) */
  isLoading: boolean;
  /** Any error that occurred */
  error: string | null;
  /** The ACS user ID of the current user */
  currentUserId: string;
  /** Manually disconnect and clean up */
  disconnect: () => void;
}

export function useAcsChat({
  config,
  threadId,
  autoConnect = true,
  onNewMessage,
}: UseAcsChatOptions): UseAcsChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serviceRef = useRef<AcsChatService | null>(null);
  const mountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const isConnectedRef = useRef(false);

  // Stable refs for config values so the effect doesn't re-run on every render
  const configRef = useRef(config);
  configRef.current = config;
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  // Track what we're currently connected to, so we only reconnect on real changes
  const connectedKeyRef = useRef<string>('');

  // --------------------------------------------------------------------------
  // Handle incoming real-time messages
  // --------------------------------------------------------------------------
  const handleNewMessage = useCallback(
    (newMsg: ChatMessage) => {
      if (mountedRef.current) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        // Notify parent component about new message (for notifications)
        if (onNewMessage && !newMsg.isCurrentUser) {
          onNewMessage(newMsg);
        }
      }
    },
    [onNewMessage]
  );

  // --------------------------------------------------------------------------
  // Connect: init client → join thread → load history → listen
  // --------------------------------------------------------------------------
  const connect = useCallback(async () => {
    const cfg = configRef.current;
    const tid = threadIdRef.current;

    if (!cfg.endpoint || !cfg.token || !cfg.userId) {
      return;
    }

    if (!tid) {
      // Don't show error during initial load - just skip connection
      // The hook will retry when threadId becomes available
      return;
    }

    // Build a connection key from the values that matter
    const connectionKey = `${cfg.endpoint}|${cfg.token}|${cfg.userId}|${tid}`;

    // If already connected with the same config, skip
    if (connectedKeyRef.current === connectionKey) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Clean up any existing service before creating new one
      if (serviceRef.current) {
        await serviceRef.current.dispose().catch(() => {
          // Ignore disposal errors
        });
        serviceRef.current = null;
        isConnectedRef.current = false;
        connectedKeyRef.current = '';
      }

      if (!mountedRef.current) return;

      const service = new AcsChatService(cfg);
      serviceRef.current = service;

      // Step 1: Connect to ACS
      await service.initClient();
      // After every async step, check if this service is still the active one
      // (user may have toggled the chat closed, disposing this service)
      if (!mountedRef.current || serviceRef.current !== service) return;

      // Step 2: Join the backend-created thread
      const threadClient = service.joinThread(tid);
      if (
        !threadClient ||
        !mountedRef.current ||
        serviceRef.current !== service
      )
        return;

      // Step 3: Load message history
      const history = await service.getMessages();
      if (!mountedRef.current || serviceRef.current !== service) return;
      setMessages(history);

      // Step 4: Listen for real-time incoming messages
      service.onMessageReceived(handleNewMessage);

      if (mountedRef.current && serviceRef.current === service) {
        connectedKeyRef.current = connectionKey;
        isConnectedRef.current = true;
        setIsConnected(true);
      }
    } catch (err) {
      // Only report errors if the component is still mounted and this service is still active
      if (mountedRef.current) {
        const message =
          err instanceof Error ? err.message : 'Failed to connect to chat.';
        // Suppress errors from disposed services (e.g. rapid open/close)
        if (!message.includes('disposed')) {
          setError(message);
        }
      }
      console.warn('[useAcsChat] Connection error:', err);
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
      isConnectingRef.current = false;
    }
  }, [handleNewMessage]);

  // --------------------------------------------------------------------------
  // Auto-connect when config/threadId become available or change
  // --------------------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;

    if (
      autoConnect &&
      config.endpoint &&
      config.token &&
      config.userId &&
      threadId
    ) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      isConnectingRef.current = false;
      isConnectedRef.current = false;
      connectedKeyRef.current = '';
      if (serviceRef.current) {
        serviceRef.current.dispose().catch((err) => {
          console.warn('[useAcsChat] Cleanup error:', err);
        });
        serviceRef.current = null;
      }
    };
  }, [autoConnect, config.endpoint, config.token, config.userId, threadId]);

  // --------------------------------------------------------------------------
  // Send a message
  // --------------------------------------------------------------------------
  const sendMessage = useCallback(async (text: string) => {
    if (!serviceRef.current) {
      setError('Chat not connected.');
      return;
    }
    try {
      await serviceRef.current.sendMessage(text);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send message.';
      setError(message);
    }
  }, []);

  // --------------------------------------------------------------------------
  // Disconnect
  // --------------------------------------------------------------------------
  const disconnect = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.dispose().catch((err) => {
        console.warn('[useAcsChat] Disconnect error:', err);
      });
      serviceRef.current = null;
    }
    isConnectedRef.current = false;
    connectedKeyRef.current = '';
    setIsConnected(false);
    setMessages([]);
  }, []);

  return {
    messages,
    sendMessage,
    isConnected,
    isLoading,
    error,
    currentUserId: config.userId,
    disconnect,
  };
}
